import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ICategory,
  IFeatureService,
  IOrganisation,
  IService,
  IHttpPagedResponse,
  IHttpSingleDataResponse,
} from '@irembo-andela/irembogov3-common';
import { IGetServiceResponse } from '../../models/services/get-service-responose';
import { Observable, map } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IServiceData,
  IServiceNextStep,
} from '../../models/applications/form-layout.model';
import { IApplicationRequest } from '../../models/applications/application-request.model';
import { IApplicationResponse } from '../../models/applications/application-response.model';
import { ICategoryServices } from '../../models/services/category-services-response.model';
import { IServiceTranslation } from '../../models/services/service-translation.model';
import { IResponse } from '../../models/common/response.model';
import { IAssignedAgentGroups } from '../../models/services/agent-group.model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  constructor(private http: HttpClient) {}

  getServices(queryParams: string) {
    return this.http.get<IHttpPagedResponse<IService>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/public/services?${queryParams}`
    );
  }

  getAssignedAgentGroups() {
    return this.http.get<IHttpSingleDataResponse<IAssignedAgentGroups>>(
      `${environment.apiGatewayBaseUrl}/agent/v1/assigned-agent-groups`
    );
  }

  getAssignedServices(queryParams: string, agentGroupIds: string[]) {
    const headers = new HttpHeaders({ agentGroupIds });
    return this.http.get<IHttpPagedResponse<IService>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/agent/services?${queryParams}`,
      { headers: headers }
    );
  }

  getFeaturedServices() {
    return this.http.get<IHttpPagedResponse<IFeatureService>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/public/featured-services`
    );
  }

  getCategories(params = ''): Observable<IHttpPagedResponse<ICategory>> {
    return this.http.get<IHttpPagedResponse<ICategory>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/categories?${params}`
    );
  }

  getOrganisations(queryParams: string) {
    return this.http.get<IHttpPagedResponse<IOrganisation>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/organizations?${queryParams}`
    );
  }

  getServiceById(serviceId: string) {
    return this.http.get<IGetServiceResponse>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/public/services/${serviceId}`
    );
  }

  getChangeRequestApplicationForm(
    changeRequestId: string
  ): Observable<FormlyFieldConfig[]> {
    return this.http
      .get<IHttpSingleDataResponse<IServiceData>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/form-definition`
      )
      .pipe(
        map(res => {
          return res.data.fields;
        })
      );
  }

  getServiceNextSteps(serviceId: string): Observable<IServiceNextStep[]> {
    return this.http
      .get<IHttpSingleDataResponse<IServiceNextStep[]>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/public/services/${serviceId}/next-steps`
      )
      .pipe(
        map(res => {
          return res.data;
        })
      );
  }

  resubmitApplication(
    application: IApplicationRequest
  ): Observable<IHttpSingleDataResponse<IApplicationResponse>> {
    return this.http.post<IHttpSingleDataResponse<IApplicationResponse>>(
      `${environment.apiGatewayBaseUrl}/application/v1/resubmit-application`,
      application
    );
  }

  getCategoryServices(): Observable<
    IHttpSingleDataResponse<ICategoryServices[]>
  > {
    return this.http.get<IHttpSingleDataResponse<ICategoryServices[]>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/category-services?applicantType=BOTH&applicantType=INDIVIDUAL`
    );
  }

  getCategoryServicesForAgent(
    agentGroupIds: string[]
  ): Observable<IHttpSingleDataResponse<ICategoryServices[]>> {
    const headers = new HttpHeaders({ agentGroupIds });
    return this.http.get<IHttpSingleDataResponse<ICategoryServices[]>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/agent/category-services?applicantType=BOTH&applicantType=INDIVIDUAL&applicantType=BUSINESS`,
      { headers: headers }
    );
  }

  getServiceTranslationsByLocale(serviceId: string, locale: string) {
    return this.http
      .get<IResponse<IServiceTranslation>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/public/services/${serviceId}/translations/${locale}`
      )
      .pipe(
        map(res =>
          this.setDefaultLocaleValue(res as unknown as Record<string, unknown>)
        )
      );
  }

  private setDefaultLocaleValue(response: Record<string, unknown>) {
    const newObject: Record<string, unknown> = {};

    Object.entries(response).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        newObject[key] = this.setDefaultLocaleValue(
          value as Record<string, unknown>
        );
      } else {
        newObject[key] =
          typeof value === 'string' && value === '' ? key : value;
      }
    });

    return newObject as unknown as IResponse<IServiceTranslation>;
  }
}
